import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAcsVisitsIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensAcsVisitsIndicatorC3";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC3CitizensAcsVisitsTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	dumDate: Column;
	dppDate: Column;
	gestationalAge: Column;
	acsHomeVisits: Column;
	threeAcsHomeVisitsStatus: Column;
	puerperalAcsHomeVisits: Column;
	puerperalAcsHomeVisitsStatus: Column;
};

type IndicatorEsfC3CitizensAcsVisitsTableProps = {
	citizensAcsVisits: DetailedCitizenAcsVisitsIndicatorC3Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenWithHomeVisits: DetailedCitizenAcsVisitsIndicatorC3Data) => void;
};

export function IndicatorEsfC3CitizensAcsVisitsTable({
	citizensAcsVisits,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC3CitizensAcsVisitsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC3CitizensAcsVisitsTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		dumDate: { title: "DUM", visible: true },
		dppDate: { title: "DPP", visible: true },
		gestationalAge: { title: "IG", visible: true },
		acsHomeVisits: { title: "Visitas - Gestação", visible: true },
		threeAcsHomeVisitsStatus: { title: "Status Visitas - Gestação", visible: true },
		puerperalAcsHomeVisits: { title: "Visitas - Puerpério", visible: true },
		puerperalAcsHomeVisitsStatus: { title: "Status Visitas - Puerpério", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.dumDate.visible && "d-none"}`}>
						{columns.dumDate.title}
					</th>
					<th className={`px-2 ${!columns.dppDate.visible && "d-none"}`}>
						{columns.dppDate.title}
					</th>
					<th className={`px-2 ${!columns.gestationalAge.visible && "d-none"}`}>
						{columns.gestationalAge.title}
					</th>
					<th className={`px-2 ${!columns.acsHomeVisits.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.acsHomeVisits.title}
							<CardOverlayTrigger
								header="Visitas - Gestação"
								message="Quantidade de visitas realizadas por um ACS durante o período gestacional (a portaria indica que sejam registradas 3 visitas)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.threeAcsHomeVisitsStatus.visible && "d-none"}`}>
						{columns.threeAcsHomeVisitsStatus.title}
					</th>
					<th className={`px-2 ${!columns.puerperalAcsHomeVisits.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.puerperalAcsHomeVisits.title}
							<CardOverlayTrigger
								header="Visitas - Puerpério"
								message="Quantidade de visitas realizadas por um ACS durante o período de puerpério (até 42 dias após a finalização do período gestacional)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.puerperalAcsHomeVisitsStatus.visible && "d-none"
						}`}
					>
						{columns.puerperalAcsHomeVisitsStatus.title}
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC3CitizensAcsVisitsTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC3CitizensAcsVisitsTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC3CitizensAcsVisitsTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensAcsVisits.length > 0
					? citizensAcsVisits.map((citizenWithAcsVisits, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAcsVisits.fullName}</div>
										<div className="text-muted">
											{citizenWithAcsVisits.birthdate
												? dayjs(citizenWithAcsVisits.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithAcsVisits.birthdate)}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.cpf
										? formatIdentifier(citizenWithAcsVisits.cpf)
										: citizenWithAcsVisits.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAcsVisits.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithAcsVisits.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dumDate.visible && "d-none"
									}`}
								>
									{dayjs(citizenWithAcsVisits.dumDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dppDate.visible && "d-none"
									}`}
								>
									{dayjs(citizenWithAcsVisits.dppDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.gestationalAge.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.gestationalAge ?? ""}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.acsHomeVisits.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.acsHomeVisits?.length ?? 0}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.threeAcsHomeVisitsStatus.visible && "d-none"
									}`}
								>
									{getCitizenScoreStatusDescriptionIndicatorC3(
										citizenWithAcsVisits.threeAcsHomeVisitsStatus
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.puerperalAcsHomeVisits.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.puerperalAcsHomeVisits?.length ?? 0}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.puerperalAcsHomeVisitsStatus.visible && "d-none"
									}`}
								>
									{getCitizenScoreStatusDescriptionIndicatorC3(
										citizenWithAcsVisits.puerperalAcsHomeVisitStatus
									)}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithAcsVisits)}
										title="Histórico de visitas do ACS"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
